import React, { useEffect, useState, useRef, useCallback } from 'react'
import styled from 'styled-components'
import { Modal, Text, Flex, Button, Slider, BalanceInput, AutoRenewIcon, Link } from '@pixel-nft/uikit'
import { useTranslation } from 'contexts/Localization'
import { useLendingPoolDeposit } from 'hooks/useStake'
import useTheme from 'hooks/useTheme'
import useToast from 'hooks/useToast'
import BigNumber from 'bignumber.js'
import { getDecimalAmount, getFullDisplayBalance } from 'utils/formatBalance'
import PercentageButton from './PercentageButton'

const StakeModal: React.FC<{
  isPxf: boolean
  poolId: number
  stakingTokenBalance: BigNumber
  onDismiss?: () => void
}> = ({ isPxf, poolId, stakingTokenBalance, onDismiss }) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { onDeposit } = useLendingPoolDeposit(poolId, isPxf)
  const { toastSuccess, toastError } = useToast()
  const [pendingTx, setPendingTx] = useState(false)
  const [stakeAmount, setStakeAmount] = useState('')
  const [percent, setPercent] = useState(0)
  const [inputStake, setInputStake] = useState(new BigNumber(0))

  const currentBalance = getFullDisplayBalance(stakingTokenBalance, 18)
  const fullBalanceNumber = new BigNumber(currentBalance)

  useEffect(() => {
    setInputStake(new BigNumber(stakeAmount))
  }, [stakeAmount])

  const handleStakeInputChange = (input: string) => {
    setInputStake(new BigNumber(input))
    if (input) {
      const convertedInput = getDecimalAmount(new BigNumber(input), 18)
      const percentage = Math.floor(convertedInput.dividedBy(stakingTokenBalance).multipliedBy(100).toNumber())
      setPercent(Math.min(percentage, 100))
    } else {
      setPercent(0)
    }
    setStakeAmount(input)
  }

  const handleChangePercent = (sliderPercent: number) => {
    if (sliderPercent > 0) {
      const percentageOfStakingMax = stakingTokenBalance.dividedBy(100).multipliedBy(sliderPercent)
      const amountToStake = getFullDisplayBalance(percentageOfStakingMax, 18)
      setStakeAmount(amountToStake)
    } else {
      setStakeAmount('')
    }
    setPercent(sliderPercent)
  }

  const handleConfirmClick = async () => {
    setPendingTx(true)
    try {
      // staking
      await onDeposit(stakeAmount)
      toastSuccess(`${t('Staked')}!`, t('Your funds have been staked in the pool!'))
      setPendingTx(false)
      onDismiss()
    } catch (e) {
      toastError(t('Canceled'), t('Please try again and confirm the transaction.'))
      setPendingTx(false)
      onDismiss()
      console.error(e)
    }
  }

  return (
    <Modal title={t('DEPOSIT')} onDismiss={onDismiss} headerBackground={theme.colors.cardHeading}>
      <Flex alignItems="center" justifyContent="space-between" mb="8px">
        <Text bold>{t('Deposit')}:</Text>
        <Flex alignItems="center">
          <Text ml="4px" bold>
            {isPxf ? 'PXF' : 'USDT'}
          </Text>
        </Flex>
      </Flex>
      <BalanceInput
        value={stakeAmount}
        onChange={(e) => {
          handleStakeInputChange(e.target.value)
        }}
        isWarning={inputStake.gt(stakingTokenBalance)}
      />
      <Text ml="auto" color="textSubtle" fontSize="12px" mb="8px">
        {t('Balance: %balance%', {
          balance: getFullDisplayBalance(stakingTokenBalance, 18),
        })}
      </Text>
      <Slider
        min={0}
        max={100}
        value={percent}
        onValueChanged={handleChangePercent}
        name="stake"
        valueLabel={`${percent}%`}
        step={1}
      />
      <Flex alignItems="center" justifyContent="space-between" mt="8px">
        <PercentageButton onClick={() => handleChangePercent(25)}>25%</PercentageButton>
        <PercentageButton onClick={() => handleChangePercent(50)}>50%</PercentageButton>
        <PercentageButton onClick={() => handleChangePercent(75)}>75%</PercentageButton>
        <PercentageButton onClick={() => handleChangePercent(100)}>MAX</PercentageButton>
      </Flex>
      <Button
        isLoading={pendingTx}
        endIcon={pendingTx ? <AutoRenewIcon spin color="currentColor" /> : null}
        onClick={handleConfirmClick}
        disabled={pendingTx || !stakeAmount || parseFloat(stakeAmount) === 0 || inputStake.gt(fullBalanceNumber)}
        mt="24px"
      >
        {pendingTx ? t('Confirming') : t('Confirm')}
      </Button>
    </Modal>
  )
}

export default StakeModal
